import { useState, useEffect, useRef } from 'react';
import ReconnectingWebSocket from 'reconnecting-websocket';

const useRecorder = () => {
    const [error, setError] = useState('');
    const [recordingState, setRecordingState] = useState('idle');
    const [audioURL, setAudioURL] = useState('');
    const [blob, setBlob] = useState(null);
    const [audioFile, setAudioFile] = useState(null);
    const [transcript, setTranscript] = useState('');

    const mediaRecorderRef = useRef();
    const streamRef = useRef(null);
    const socketRef = useRef(null);

    const startCapture = async (socket) => {
        try {
            const media = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = media;
            mediaRecorderRef.current = new MediaRecorder(media, { mimeType: 'audio/webm' });

            mediaRecorderRef.current.start(1500);
            setRecordingState('recording');
            localStorage.setItem('time', Date.now());

            mediaRecorderRef.current.addEventListener('dataavailable', (evt) => {
                if (evt.data.size > 0 && socket.readyState === WebSocket.OPEN) {
                    socket.send(evt.data);
                }
            });
        } catch (e) {
            console.error('Error in capturing data:', e);
            setError('Error in capturing data.');
        }
    };

    const startRecording = async () => {
        try {
            const socket = new ReconnectingWebSocket(
                'wss://api.deepgram.com/v1/listen?model=nova-2-medical&smart_format=true',
                ['token', process.env.REACT_APP_DEEPGRAM_KEY]
            );
            socketRef.current = socket;

            socket.onopen = () => {
                console.log('Connected Successfully', socket);
                startCapture(socket);

                setInterval(() => {
                    if (socket.readyState === WebSocket.OPEN) {
                        socket.send(JSON.stringify({ type: 'KeepAlive' }));
                    }
                }, 10000);
            };

            socket.onmessage = (msg) => {
                try {
                    const { transcript: transcriptData } = JSON.parse(msg.data)?.channel?.alternatives[0];

                    if (transcriptData) {
                        setTranscript((prev) => `${prev} ${transcriptData}`);
                    }
                } catch (e) {
                    console.error('Error parsing message:', e);
                }
            };

            socket.onclose = () => {
                console.log('WebSocket closed');
                stopRecording();
            };

            socket.onerror = (error) => {
                console.error('WebSocket encountered an error:', error);
                stopRecording();
            };
        } catch (e) {
            console.error('Error starting recording:', e);
            setError('Error starting recording.');
        }
    };

    const stopMediaRecorder = () => {
        if (mediaRecorderRef.current?.state !== 'inactive') {
            mediaRecorderRef.current?.stop();
        }
    };

    const closeAllTracks = () => {
        if (!streamRef.current) {
            console.error('Stream is invalid or null.');
            return;
        }

        streamRef.current.getTracks().forEach((track) => track.stop());
    };

    const closeSocket = () => {
        if (socketRef.current) {
            socketRef.current.close();
        }
    };

    const pauseRecording = () => {
        setRecordingState('idle');
        mediaRecorderRef.current?.pause();
    };

    const resumeRecording = () => {
        setRecordingState('recording');
        mediaRecorderRef.current?.resume();
    };

    const resetRecording = () => {
        pauseRecording();
        setTranscript('');
        stopRecording();
    };

    const stopRecording = () => {
        setRecordingState('idle');
        stopMediaRecorder();
        closeAllTracks();
        closeSocket();
    };

    return {
        startRecording,
        pauseRecording,
        resumeRecording,
        stopRecording,
        resetRecording,
        recordingState,
        isRecording: recordingState === 'recording',
        audioURL,
        audioFile,
        audioBlob: blob,
        transcript,
        setTranscript,
        error,
        setError,
    };
};

export default useRecorder;
